import { useState } from "react";
import { Dialog, DialogContent, Divider, Link, Typography } from "@mui/material";
import CustomPrimaryButton from "@fuse/core/CustomPrimaryButton/CustomPrimaryButton";
import useThemeMediaQuery from "@fuse/hooks/useThemeMediaQuery";
import RemoveRecordDialog from "./RemoveRecordDialog";

interface RecoverActivateModalProps {
	petName: string;
	chip: string;
	email: string;
	onActivate: () => void;
	onRemove?: () => void;
}

function RecoverActivateModal({ petName: rawPetName, chip, email, onActivate, onRemove }: RecoverActivateModalProps) {
	const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down("sm"));
	const petName = rawPetName ? rawPetName.charAt(0).toUpperCase() + rawPetName.slice(1).toLowerCase() : "";
	const [showRemoveDialog, setShowRemoveDialog] = useState(false);

	const content = (
		<div className={`flex flex-col ${isMobile ? "gap-3" : "gap-6"}`}>
			{/* Logo */}
			<div className="flex justify-center">
				<img src="assets/img/checkout-logo.svg" alt="Peeva" style={{ width: 64 }} />
			</div>

			{/* Title */}
			<div className="flex flex-col items-center gap-1">
				<Typography variant="caption" align="center" color="text.secondary" fontWeight={600}>
					&#128274; Secure record lookup — fetch.peeva.co
				</Typography>
				<Typography variant="h6" fontWeight={700} align="center" sx={{ fontSize: 22 }}>
					Microchip Record Found — Owner Not Verified
				</Typography>
			</div>

			{/* Pet Record Box */}
			<div style={{ border: "1px solid #ccc", borderRadius: 8, padding: "16px" }}>
				<div className="flex flex-col gap-2">
					<Typography variant="body2" fontWeight={700} sx={{ fontSize: 17 }}>Pet Record</Typography>
					<Typography variant="body2" sx={{ fontSize: 17 }}>Pet: <strong>{petName}</strong></Typography>
					<Typography variant="body2" sx={{ fontSize: 17 }}>Microchip ID: <strong>{chip}</strong></Typography>
					<Typography variant="body2" sx={{ fontSize: 17, color: "#ED6C02" }}>
						Status: Owner Record Not Verified
					</Typography>
				</div>
			</div>

			{/* Body paragraphs */}
			<div className="flex flex-col">
				<Typography variant="caption" align="center" sx={{ py: 1.5, fontSize: 11 }}>
					This microchip record exists in the Peeva central registry but is not currently linked to a verified owner. Records typically originate from veterinary clinics, animal shelters, or prior registration attempts.
				</Typography>
			</div>

			{/* Pricing */}
			<div className="flex flex-col items-center gap-2" style={{ border: "1px solid #ccc", borderRadius: 8, padding: "5px 16px" }}>
				<Typography variant="h6" fontWeight={700}>
					Activate Microchip
				</Typography>
				<Typography variant="h4" fontWeight={700}>
					$104{" "}
					<Typography component="span" variant="body2" fontWeight={600}>one-time owner record activation</Typography>
				</Typography>
				<Typography variant="body2" align="center">
					Protects this microchip record for your pet's lifetime
				</Typography>
				<Typography variant="body2" fontWeight={600} color="text.secondary" align="center">
					No subscription &middot; No recurring fees
				</Typography>
			</div>

			{/* Not your pet */}
			<div className="flex flex-col items-center gap-2" style={{ padding: "6px 0" }}>
				<Divider sx={{ width: "100%" }} />
				<Typography variant="body2" align="center">
					Not your pet?{" "}
					<Link component="button" onClick={() => setShowRemoveDialog(true)} underline="hover" sx={{ fontWeight: 600, color: "#52BFF4", verticalAlign: "baseline" }}>
						Remove this record
					</Link>
				</Typography>
			</div>

			{/* CTA */}
			<div style={isMobile ? { position: "sticky", bottom: 0, background: "#fff", paddingTop: 8, paddingBottom: 8 } : undefined}>
				<CustomPrimaryButton onClick={onActivate} fullWidth>
					Activate {petName}'s Microchip
				</CustomPrimaryButton>
			</div>

			{/* Payment icons */}
			<div className="flex justify-center gap-3 flex-wrap">
				{[
					{ src: "assets/images/payment/37.png", alt: "Apple Pay" },
					{ src: "assets/images/payment/38.png", alt: "Google Pay" },
					{ src: "assets/images/payment/39.png", alt: "Visa" },
					{ src: "assets/images/payment/40.png", alt: "Mastercard" },
					{ src: "assets/images/payment/44.png", alt: "Klarna" },
				].map(({ src, alt }) => (
					<img key={alt} src={src} alt={alt} style={{ height: 28, width: "auto" }} />
				))}
			</div>

			{/* Stripe notice */}
			<Typography variant="caption" align="center" color="text.secondary">
				Secure checkout powered by{" "}
				<span style={{ color: "#635BFF", fontWeight: 700 }}>Stripe</span>
			</Typography>

			<Typography variant="caption" color="text.secondary" align={isMobile ? "center" : undefined} sx={{ fontSize: 10 }} fontWeight={600}>
				By continuing you confirm this is your pet and want to activate this microchip record with Peeva.
			</Typography>
		</div>
	);

	const removeDialog = (
		<RemoveRecordDialog
			open={showRemoveDialog}
			petName={petName}
			chip={chip}
			email={email}
			onCancel={() => setShowRemoveDialog(false)}
			onDone={() => {
				setShowRemoveDialog(false);
				onRemove?.();
			}}
		/>
	);

	if (isMobile) {
		return (
			<>
				<div
					style={{
						position: "fixed",
						inset: 0,
						overflowY: "auto",
						background: "#fff",
						zIndex: 1300,
						padding: "24px 16px",
					}}
				>
					<div className="max-w-sm mx-auto">
						{content}
					</div>
				</div>
				{removeDialog}
			</>
		);
	}

	return (
		<>
			<Dialog open maxWidth="sm" fullWidth disableEscapeKeyDown
				PaperProps={{ sx: { borderRadius: 3, px: 2, py: 0.5, maxWidth: 640 } }}>
				<DialogContent>{content}</DialogContent>
			</Dialog>
			{removeDialog}
		</>
	);
}

export default RecoverActivateModal;
